import { API_PREFIX, HttpError, type HttpClient } from "@nasam/kernel";
import { paginatedFromDto } from "@nasam/kernel";
import type {
  AccountAdminsRepository,
  CompaniesRepository,
  CompanyWriteInput,
} from "../domain";
import {
  accountAdminCreateToDto,
  accountAdminFromDto,
  accountAdminUpdateToDto,
  archivedAdminFromDto,
  archivedCompanyFromDto,
  companyCreateToDto,
  companyFromDto,
  companySummaryFromDto,
  companyUpdateToDto,
} from "./organizationMappers";
import {
  accountAdminCreateWriteDtoSchema,
  accountAdminListResponseDtoSchema,
  accountAdminResponseDtoSchema,
  accountAdminUpdateWriteDtoSchema,
  archivedAdminListResponseDtoSchema,
  archivedAdminMatchResponseDtoSchema,
  archivedCompanyListResponseDtoSchema,
  archivedCompanyMatchResponseDtoSchema,
  companyCreateWriteDtoSchema,
  companyListResponseDtoSchema,
  companyResponseDtoSchema,
  companySummaryResponseDtoSchema,
  companyUpdateWriteDtoSchema,
  deleteCompanyResponseDtoSchema,
  messageResponseDtoSchema,
  resendActivationLinkWriteDtoSchema,
} from "./organizationDtos";

const ACCOUNTS_PATH = `${API_PREFIX}/accounts`;
const ADMINS_PATH = `${API_PREFIX}/users/accounts-admins`;

function companyMultipartBody(
  dto: Record<string, unknown>,
  input: CompanyWriteInput,
): FormData {
  const body = new FormData();

  for (const [key, value] of Object.entries(dto)) {
    if (key === "attachmentsIdsToBeDeleted" || value === undefined) continue;
    if (typeof value !== "string") continue;
    body.set(key, value);
  }

  for (const attachment of input.attachments ?? []) {
    const file = attachment.file;
    if (file !== undefined) {
      body.append("attachments", file, attachment.fileName);
    }
  }

  // multer's field parser (append-field) only arrays a repeated key when it
  // sees the trailing `[]` -- a bare repeated key collapses to a plain string
  // when there's exactly one value, which fails the backend's `@IsArray()`
  // check. The `[]` is stripped before the DTO sees the field name.
  for (const id of input.attachmentsIdsToBeDeleted ?? []) {
    body.append("attachmentsIdsToBeDeleted[]", id);
  }

  return body;
}

export function createCompaniesHttpRepository(
  http: HttpClient,
): CompaniesRepository {
  return {
    async list(query) {
      const response = await http.get(ACCOUNTS_PATH, { params: query });
      return paginatedFromDto(
        companyListResponseDtoSchema.parse(response),
        companyFromDto,
      );
    },

    async listArchived(query) {
      const response = await http.get(`${ACCOUNTS_PATH}/archived`, {
        params: query,
      });
      return paginatedFromDto(
        archivedCompanyListResponseDtoSchema.parse(response),
        archivedCompanyFromDto,
      );
    },

    async getSummary() {
      const response = await http.get(`${ACCOUNTS_PATH}/summary`);
      return companySummaryFromDto(
        companySummaryResponseDtoSchema.parse(response).data,
      );
    },

    async getById(id) {
      const response = await http.get(`${ACCOUNTS_PATH}/${id}`);
      return companyFromDto(companyResponseDtoSchema.parse(response).data);
    },

    async create(input) {
      const dto = companyCreateWriteDtoSchema.parse(companyCreateToDto(input));
      try {
        const response = await http.post(
          ACCOUNTS_PATH,
          companyMultipartBody(dto, input),
        );
        const created = companyResponseDtoSchema.parse(response);
        const company = companyFromDto(created.data);
        return {
          status: "created",
          company,
          ...(created.message !== undefined
            ? { responseMessage: created.message }
            : {}),
        };
      } catch (error) {
        // Used to answer 201 with a trimmed match (missing the required
        // type/createdAt/currencyId) when the name/email/phone matched an
        // archived account; now 409s instead, same trimmed shape under
        // `data` -- still not an error from the form's own perspective,
        // just an offer to restore. Disambiguated by shape, not the code
        // string, same as every other archived-match endpoint in this app.
        const archived =
          error instanceof HttpError && error.status === 409
            ? archivedCompanyMatchResponseDtoSchema.safeParse(error.data)
            : undefined;
        if (archived === undefined || !archived.success) throw error;
        return {
          status: "archived-match",
          match: {
            id: archived.data.data.id,
            nameEn: archived.data.data.nameEn,
            nameAr: archived.data.data.nameAr,
            email: archived.data.data.email,
            phoneNumber1: archived.data.data.phoneNumber1,
          },
          message: archived.data.message,
        };
      }
    },

    async update(id, input) {
      const dto = companyUpdateWriteDtoSchema.parse(companyUpdateToDto(input));
      const response = await http.patch(
        `${ACCOUNTS_PATH}/${id}`,
        companyMultipartBody(dto, input),
      );
      return companyFromDto(companyResponseDtoSchema.parse(response).data);
    },

    async delete(id) {
      const response = await http.delete(`${ACCOUNTS_PATH}/${id}`);
      return deleteCompanyResponseDtoSchema.parse(response).data;
    },

    async restore(id) {
      const response = await http.patch(`${ACCOUNTS_PATH}/${id}/restore`);
      const parsed = companyResponseDtoSchema.parse(response);
      const company = companyFromDto(parsed.data);
      return {
        ...company,
        ...(parsed.message !== undefined
          ? { responseMessage: parsed.message }
          : {}),
      };
    },
  };
}

export function createAccountAdminsHttpRepository(
  http: HttpClient,
): AccountAdminsRepository {
  return {
    async list(query) {
      const response = await http.get(ADMINS_PATH, { params: query });
      return paginatedFromDto(
        accountAdminListResponseDtoSchema.parse(response),
        accountAdminFromDto,
      );
    },

    async listArchived(query) {
      const response = await http.get(`${ADMINS_PATH}/archived`, {
        params: query,
      });
      return paginatedFromDto(
        archivedAdminListResponseDtoSchema.parse(response),
        archivedAdminFromDto,
      );
    },

    async getById(id) {
      const response = await http.get(`${ADMINS_PATH}/${id}`);
      return accountAdminFromDto(
        accountAdminResponseDtoSchema.parse(response).data,
      );
    },

    async create(input) {
      const body = accountAdminCreateWriteDtoSchema.parse(
        accountAdminCreateToDto(input),
      );
      try {
        const response = await http.post(
          `${API_PREFIX}/users/account-admin`,
          body,
        );
        const created = accountAdminResponseDtoSchema.parse(response);
        const admin = accountAdminFromDto(created.data);
        return {
          status: "created",
          admin,
          ...(created.message !== undefined
            ? { responseMessage: created.message }
            : {}),
        };
      } catch (error) {
        // Same 201-for-two-outcomes-turned-409 shape as `POST /accounts`:
        // used to answer 201 with a trimmed match (missing the required
        // phoneNumber/enable2FA/createdAt/accountId) when it matched an
        // archived admin; now 409s instead, same trimmed shape under `data`
        // -- still not an error from the form's own perspective, just an
        // offer to restore. Disambiguated by shape, not the code string,
        // same as every other archived-match endpoint in this app.
        const archived =
          error instanceof HttpError && error.status === 409
            ? archivedAdminMatchResponseDtoSchema.safeParse(error.data)
            : undefined;
        if (archived === undefined || !archived.success) throw error;
        return {
          status: "archived-match",
          match: { id: archived.data.data.id },
          message: archived.data.message,
        };
      }
    },

    async update(id, input) {
      const body = accountAdminUpdateWriteDtoSchema.parse(
        accountAdminUpdateToDto(input),
      );
      const response = await http.patch(`${ADMINS_PATH}/${id}`, body);
      return accountAdminFromDto(
        accountAdminResponseDtoSchema.parse(response).data,
      );
    },

    async delete(id) {
      const response = await http.delete(`${ADMINS_PATH}/${id}`);
      messageResponseDtoSchema.parse(response);
    },

    async restore(id) {
      const response = await http.patch(`${ADMINS_PATH}/${id}/restore`);
      const parsed = accountAdminResponseDtoSchema.parse(response);
      const admin = accountAdminFromDto(parsed.data);
      return {
        ...admin,
        ...(parsed.message !== undefined
          ? { responseMessage: parsed.message }
          : {}),
      };
    },

    async resendActivation(email) {
      const body = resendActivationLinkWriteDtoSchema.parse({ email });
      const response = await http.post(
        `${API_PREFIX}/auth/resend-activation-link`,
        body,
      );
      messageResponseDtoSchema.parse(response);
    },
  };
}
